import axiosInstance from '../axiosInstance';

import { queryClient } from '../../components/queryClient';
import { getTenantSettings, TENANT_SETTINGS_QUERY_KEY } from '../tenantSettings/tenantSettingsService';
import { defaultSiteIdQueryOptions } from '../iniTable/iniTableService';
import { notifyAuthChanged } from '../../theme/colorModeStorage';
import { clearSessionSchedule, noteAccessTokenLifetime } from '../../auth/sessionRefresh';

export const MANUAL_LOGOUT_KEY = 'manualLogout';

/** Persist /me payload for non-React-Query consumers (audit fields, etc.). */
export function syncMeToLocalStorage(me) {
    if (!me) return;
    localStorage.setItem('username', me.username ?? '');
    localStorage.setItem('roleId', String(me.roleId ?? ''));
    localStorage.setItem('permissions', JSON.stringify(me.permissions ?? []));
    localStorage.setItem('userKey', String(me.userKey ?? me.userkey ?? ''));
    notifyAuthChanged();
}

function applyExpiresIn(payload) {
    const raw = payload?.expiresIn ?? payload?.ExpiresIn;
    const seconds = Number(raw);
    if (Number.isFinite(seconds) && seconds > 0) {
        noteAccessTokenLifetime(seconds);
    }
}

async function handleAuthResponse(promise) {
    const { data } = await promise;

    // data should contain: username, roleId, permissions
    // (token may be present in DEV for Swagger; ignore it in SPA)
    const { username: user, roleId, permissions, userKey } = data || {};
    const me = { username: user, roleId, permissions, userKey };

    applyExpiresIn(data);

    // cache user info in React Query (so RequireAuth / UI can use it immediately)
    queryClient.setQueryData(['me'], me);

    // optional UI cache (not security)
    syncMeToLocalStorage(me);
    localStorage.removeItem(MANUAL_LOGOUT_KEY);

    // Prefetch tenant settings / default SiteId, but do not fail login if they error —
    // auth cookies are already set at this point.
    try {
        await queryClient.prefetchQuery({
            queryKey: TENANT_SETTINGS_QUERY_KEY,
            queryFn: ({ signal }) => getTenantSettings(signal),
            staleTime: Infinity,
            gcTime: Infinity,
        });
    } catch (e) {
        console.warn('Tenant settings prefetch after login failed', e);
    }

    try {
        await queryClient.prefetchQuery(defaultSiteIdQueryOptions);
    } catch (e) {
        console.warn('Default SiteId prefetch after login failed', e);
    }

    return { username: user, roleId, permissions, userKey };
}

export const login = async (username, password) => {
    // cookie is set by Set-Cookie (HttpOnly) — nothing to store in JS
    return handleAuthResponse(
        axiosInstance.post('webusers/login', { username, password })
    );
};

export const loginWithWindows = async () => {
    // Empty JSON object (not null): axios default Content-Type is application/json,
    // and a null/empty body with that header can produce HTTP 400 on ASP.NET.
    // Auth identity comes from Negotiate, not the body.
    return handleAuthResponse(
        axiosInstance.post('webusers/windows-login', {}, { withCredentials: true })
    );
};

/** AD username/password login (custom SPA modal). Domain optional when login-config provides defaultDomain. */
export const loginWithWindowsPassword = async ({ domain, username, password }) => {
    return handleAuthResponse(
        axiosInstance.post(
            'webusers/windows-password-login',
            { domain: domain || null, username, password },
            { withCredentials: true }
        )
    );
};

export const getMe = async (signal = null) => {
    const { data } = await axiosInstance.get('webusers/me', { signal });
    const raw = data?.expiresIn ?? data?.ExpiresIn;
    const seconds = Number(raw);
    noteAccessTokenLifetime(
        Number.isFinite(seconds) && seconds > 0 ? seconds : undefined
    );
    return data; // { username, roleId, permissions, userKey, expiresIn? }
};

export const getWindowsGroups = async (signal = null) => {
    const { data } = await axiosInstance.get('webusers/windows-groups', { signal });
    return data; // [{ groupName, groupSid }]
};

export const logout = async () => {
    try {
        await axiosInstance.post('webusers/logout', null);
    } finally {
        clearSessionSchedule();
        localStorage.setItem(MANUAL_LOGOUT_KEY, '1');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('username');
        localStorage.removeItem('roleId');
        localStorage.removeItem('permissions');
        localStorage.removeItem('userKey');
        queryClient.removeQueries({ queryKey: ['me'] });
        notifyAuthChanged();
    }
};
